import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { TimeSlotGrid } from '../reschedule/TimeSlotGrid';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import { fetchAvailableSlots, rescheduleConsultation } from '../../api/cabinetApi';
import type { Consultation } from '../../types/cabinet.types';
import { RescheduleDoctorSelect } from './RescheduleDoctorSelect';
import { RescheduleCalendar } from './RescheduleCalendar';

/* ── Icons ──────────────────────────────────────────────────────── */
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const CalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ── Helpers ─────────────────────────────────────────────────────── */
function parseDateStr(dateStr: string) {
  if (!dateStr) return null;
  const d = new Date(dateStr.replace(' ', 'T'));
  return isNaN(d.getTime()) ? null : d;
}

/* ── Component ───────────────────────────────────────────────────── */
export const ReschedulePage: React.FC<{
  onBack: () => void;
  consultationId: string;
  consultation?: Consultation | null;
}> = ({ onBack, consultationId, consultation }) => {
  const { t, lang } = useI18n();

  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate]         = useState<Date | null>(null);
  const [selectedSlotId, setSelectedSlotId]     = useState<string | null>(null);
  const [selectedSlotDoctorId, setSelectedSlotDoctorId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [isLoading, setIsLoading]               = useState(false);
  const [successNewTime, setSuccessNewTime]     = useState<string | null>(null);

  const [doctors, setDoctors] = useState<{ id: string; name: string; avatarUrl: string }[]>([]);
  const [slots, setSlots]     = useState<{ id: string; time: string; available: boolean; doctorId: string }[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);

  /* ── Validate access ───────────────────────────────────────────── */
  useEffect(() => {
    if (consultation === null) {
      onBack();
    } else if (consultation) {
      const isUpcoming = consultation.status === 'upcoming';
      const cardType = consultation.cardType ?? 'consultation';
      const canReschedule = isUpcoming && consultation.rescheduleAvailable && cardType === 'consultation';
      
      if (!canReschedule) {
        onBack();
      }
    }
  }, [consultation, onBack]);

  /* ── Load available slots ───────────────────────────────────────── */
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await fetchAvailableSlots(consultationId);
        if (data && data.length > 0) {
          setRawData(data);
          const map = new Map<string, { id: string; name: string; avatarUrl: string }>();
          data.forEach((day: any) => {
            day.Rows?.forEach((r: any) => {
              if (!map.has(r.DoctorId?.toString())) {
                map.set(r.DoctorId?.toString(), { id: r.DoctorId.toString(), name: r.DoctorFullName, avatarUrl: '' });
              }
            });
          });
          setDoctors(Array.from(map.values()));
        } else {
          setDoctors([]);
          setSlots([]);
        }
      } catch {
        toast(t('err_load_slots') || 'Wystąpił błąd podczas ładowania terminów.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [consultationId]);

  /* ── Derive available dates ─────────────────────────────────────── */
  const availableDates = React.useMemo(() => {
    return rawData
      .filter(d => d.Rows?.some((r: any) => !selectedDoctorId || r.DoctorId?.toString() === selectedDoctorId))
      .map(d => new Date(d.Date));
  }, [rawData, selectedDoctorId]);

  /* ── Auto-select first available date ──────────────────────────── */
  useEffect(() => {
    if (availableDates.length > 0) {
      if (!selectedDate || !availableDates.some(d => d.getTime() === selectedDate.getTime())) {
        setSelectedDate(availableDates[0]);
      }
    } else {
      setSelectedDate(null);
    }
  }, [availableDates]);

  /* ── Derive time slots for selected date ────────────────────────── */
  useEffect(() => {
    if (!selectedDate || rawData.length === 0) { setSlots([]); return; }
    
    // Fix timezone shift when comparing dates
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const dayObj = rawData.find(d => d.Date.startsWith(dateStr));
    if (!dayObj) { setSlots([]); return; }

    const filtered = dayObj.Rows.filter((r: any) => !selectedDoctorId || r.DoctorId?.toString() === selectedDoctorId);
    // key: time string -> first matching row (for slot ID & doctorId)
    const timeMap = new Map<string, { slotId: string; doctorId: string }>();
    filtered.forEach((r: any) => {
      const t = r.Time.includes('T') ? r.Time.split('T')[1].substring(0, 5) : r.Time;
      if (!timeMap.has(t)) timeMap.set(t, { slotId: r.Time, doctorId: r.DoctorId?.toString() ?? '' });
    });
    setSlots(Array.from(timeMap.entries())
      .map(([time, { slotId, doctorId }]) => ({ id: slotId, time, available: true, doctorId }))
      .sort((a, b) => a.time.localeCompare(b.time)));
  }, [selectedDoctorId, selectedDate, rawData]);

  /* ── Reset slot on date change ──────────────────────────────────── */
  useEffect(() => { setSelectedSlotId(null); setSelectedSlotDoctorId(null); }, [slots]);

  /* ── Confirm ─────────────────────────────────────────────────────── */
  const handleConfirm = async () => {
    if (!selectedSlotId) return;
    setIsSubmitting(true);
    try {
      await rescheduleConsultation(consultationId, selectedSlotId, selectedSlotDoctorId ?? undefined);
      const d = new Date(selectedSlotId);
      const dateStr = d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' });
      const timeStr = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      setSuccessNewTime(`${dateStr}, ${timeStr}`);
    } catch (err: any) {
      toast(err.response?.data?.message || t('err_resch') || 'Błąd podczas zmiany terminu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Success screen ──────────────────────────────────────────────── */
  if (successNewTime) {
    return (
      <div className="rp-wrap">
        <div className="rp-success-container">
          <div className="rp-success-box">
            <div className="rp-success-icon"><CheckIcon /></div>
            <h1 className="rp-success-title">{t('resch_ok_t') || 'Termin przełożony'}</h1>
            <p className="rp-success-sub">
              {t('resch_ok_p1', { date: successNewTime })}<br className="rp-desktop-only" /> {t('resch_ok_p2')}
            </p>
          </div>
          <Button variant="primary" onClick={onBack} className="rp-success-btn">
            {t('back_cons') || 'Wróć do konsultacji'}
          </Button>
        </div>
        <style>{`
          .rp-success-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            font-family: 'Poppins', sans-serif;
            width: 100%;
          }
          .rp-success-box {
            background: #E6D6FA;
            border-radius: 24px;
            width: 826px;
            max-width: 100%;
            padding: 80px 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            margin-bottom: 32px;
          }
          .rp-success-icon {
            width: 64px;
            height: 64px;
            background: #361777;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 24px;
          }
          .rp-success-icon svg {
            width: 28px;
            height: 28px;
          }
          .rp-success-title {
            font-size: 28px;
            font-weight: 700;
            color: #361777;
            margin: 0 0 16px;
          }
          .rp-success-sub {
            font-size: 16px;
            color: #361777;
            line-height: 1.5;
            margin: 0;
            max-width: 450px;
            font-weight: 400;
          }
          .rp-success-btn {
            width: 100%;
            max-width: 320px;
            border-radius: 100px !important;
            background: #361777 !important;
            border-color: #361777 !important;
            padding: 12px 24px !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            min-height: 54px !important;
          }
          .rp-success-btn:hover {
            background: #1A1133 !important;
          }
          @media (max-width: 768px) {
            .rp-success-box {
               padding: 40px 20px;
               border-radius: 24px;
            }
            .rp-success-title {
              font-size: 24px;
            }
            .rp-success-sub {
              font-size: 15px;
            }
            .rp-desktop-only {
              display: none !important;
            }
          }
        `}</style>
      </div>
    );
  }

  /* ── Parsed current visit date ───────────────────────────────────── */
  const visitDate = consultation?.date ? parseDateStr(consultation.date) : null;
  const visitDateStr = visitDate
    ? visitDate.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  const visitTimeStr = visitDate
    ? visitDate.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
    : null;

  /* ── Current visit card (reusable) ──────────────────────────────── */
  const VisitCard = ({ mobile = false }: { mobile?: boolean }) => {
    if (!consultation) return null;

    let dispDate = visitDateStr;
    let dispTime = visitTimeStr;
    let dispDoctorName = consultation.doctorName;
    let dispDoctorAvatar = consultation.doctorAvatar;

    if (selectedSlotId) {
      const selDate = new Date(selectedSlotId);
      dispDate = selDate.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
      dispTime = selDate.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      
      if (selectedSlotDoctorId) {
        const doc = doctors.find(d => d.id === selectedSlotDoctorId);
        if (doc) {
          dispDoctorName = doc.name;
          dispDoctorAvatar = doc.avatarUrl;
        }
      }
    }

    return (
      <div className={`rp-visit-card ${mobile ? 'rp-visit-card-mobile' : ''}`}>
        <div className="rp-vc-top">
          <div className="rp-vc-avatar">
            {dispDoctorAvatar
              ? <img src={dispDoctorAvatar} alt={dispDoctorName} />
              : <span>{dispDoctorName ? dispDoctorName.charAt(0) : ''}</span>
            }
          </div>
          <div className="rp-vc-details">
            <div className="rp-vc-name">{dispDoctorName}</div>
            <div className="rp-vc-spec">{consultation.serviceName || t('med_cons') || 'Konsultacja medyczna'}</div>
          </div>
        </div>
        
        {mobile && <div className="rp-vc-divider" />}

        <div className="rp-vc-actions">
          {dispDate && (
            <div className="rp-vc-datetime-row">
              <span className="rp-vc-meta-item">
                <CalIcon /> {dispDate}
                {mobile && dispTime && ` — ${dispTime}`}
              </span>
              {!mobile && dispTime && (
                <span className="rp-vc-meta-item">
                  <ClockIcon /> {dispTime}
                </span>
              )}
            </div>
          )}
          {mobile && consultation.orderId && (
            <span className="rp-vc-order">{t('order_no') || 'Nr zamówienia'} <strong>#{consultation.orderId}</strong></span>
          )}
          {!mobile && (
            <Button
              variant="primary"
              disabled={!selectedSlotId || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleConfirm}
              className="rp-confirm-btn"
            >
              {t('resch_confirm') || 'Potwierdź nowy termin'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  /* ── Form content ────────────────────────────────────────────────── */
  const FormContent = ({ isMobile = false }) => (
    <>
      <div className="rp-field rp-field-doctor">
        <div className="rp-field-label">{t('resch_doctor') || 'Wybierz lekarza'}</div>
        <RescheduleDoctorSelect doctors={doctors} selectedId={selectedDoctorId} onSelect={setSelectedDoctorId} t={t} />
      </div>

      <div className={`rp-field ${isMobile ? 'rp-field-inline-header' : ''}`}>
        <div className="rp-field-label">{t('resch_pick') || 'Wybierz termin'}</div>
        <div className="rp-cal-wrapper">
          {isLoading ? (
            <div className="rp-loading">{t('loading_slots') || 'Ładowanie terminów…'}</div>
          ) : availableDates.length > 0 ? (
            <RescheduleCalendar availableDates={availableDates} selectedDate={selectedDate} onSelect={setSelectedDate} t={t} lang={lang} />
          ) : (
            <div className="rp-no-slots">{t('no_avail_slots') || 'Brak dostępnych terminów'}</div>
          )}
        </div>
      </div>

      {slots.length > 0 && (
        <div className="rp-field rp-field-slots">
          <TimeSlotGrid slots={slots} selectedId={selectedSlotId} onSelect={(id) => {
            setSelectedSlotId(id);
            const slot = slots.find(s => s.id === id);
            setSelectedSlotDoctorId(slot?.doctorId ?? null);
          }} />
        </div>
      )}

      {isMobile && (
        <Button
          variant="primary"
          disabled={!selectedSlotId || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleConfirm}
          className="rp-mobile-confirm-btn"
        >
          {t('resch_confirm') || 'Potwierdź nowy termin'}
        </Button>
      )}
    </>
  );

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <div className="rp-wrap">

      {/* ─── MOBILE ONLY: back button ───────────────────────────── */}
      <button className="rp-back-btn" onClick={onBack}>
        <BackIcon /> <span>{t('go_back') || 'Wróć'}</span>
      </button>

<div className="page-top">
      {/* Page title */}
      <h1 className="rp-page-title">{t('resch_title') || 'Przełóż wizytę'}</h1>
      <p className="rp-page-sub">{t('resch_intro') || 'Zmieniły się Twoje plany? Przełóż konsultację w kilka kliknięć'}</p>


      {/* ─── DESKTOP LAYOUT ──────────────────────────────────────── */}
      <div className="rp-desktop">
        {/* Big purple form box */}
        <div className="rp-purple-box">
          <FormContent />
        </div>
</div>
       
      
</div>

 {/* Current visit + button below */}
        {consultation && selectedSlotId && (
          <div className="rp-current-section rp-desktop-only">
            <div className="rp-current-label">{t('resch_new_time') || 'Twój nowy termin'}</div>
            <VisitCard />
          </div>
        )}


      {/* ─── MOBILE LAYOUT ───────────────────────────────────────── */}
      <div className="rp-mobile">
        {/* Form area with rounded background */}
        <div className="rp-mobile-form-wrap">
          {consultation && selectedSlotId && (
            <div className="rp-mobile-current-section">
              <div className="rp-mobile-current-label">{t('resch_new_time') || 'Twój nowy termin'}</div>
              <VisitCard mobile />
            </div>
          )}
          <FormContent isMobile />
        </div>
      </div>

      {/* ─── STYLES ──────────────────────────────────────────────── */}
      <style>{`
        /* ── wrapper ── */
        .rp-wrap {
          font-family: 'Poppins', sans-serif;
          max-width: 100%;
          padding-bottom: 48px;
        }



        /* ── back btn ── */
        .rp-vc-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #F8F6FC;
          border-radius: 24px;
          padding: 24px 32px;
          border: none;
        }
        .rp-back-btn {
          display: none;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          cursor: pointer;
          color: #AB9CC9;
          font-size: 14px;
          font-weight: 500;
          font-family: 'Poppins', sans-serif;
          margin-bottom: 16px;
          padding: 0;
        }

        /* ── page title ── */
        .rp-page-title {
          font-size: 32px;
          font-weight: 700;
          color: #361777;
          margin: 0 0 8px;
        }
        .rp-page-sub {
          font-size: 15px;
          color: #361777;
          margin: 0 0 32px;
          line-height: 1.5;
        }
        .page-top {
          background: #E6D6FA;
          padding: 40px 64px;
          border-radius: 24px;
          max-width: 100%;
          width: 994px;
        }

        /* ── DESKTOP ── */
        .rp-mobile { display: none; }
        .rp-desktop { 
          display: flex; 
          flex-direction: column; 
          gap: 40px; 
          max-width: 100%;
          margin-top: 32px;
        }

        /* Purple form box */
        .rp-purple-box {
          display: flex;
          flex-direction: column;
          gap: 24px;
          max-width: 100%;
        }

        /* Current visit section */
        .rp-current-section {
          display: flex;
          flex-direction: column;
          gap: 24px;
          background: #FAF8FE;
          border-radius: 24px;
          padding: 24px 64px;
          margin-top: 16px;
        }
        .rp-current-label {
          font-size: 20px;
          font-weight: 700;
          color: #361777;
        }

        /* ── form fields ── */
        .rp-field {}
        .rp-field-label {
          font-size: 15px;
          font-weight: 700;
          color: #361777;
          margin-bottom: 16px;
        }
        .rp-field-slots {
          margin-top: 32px;
          width: 100%;
          overflow: hidden;
        }
        .rp-loading, .rp-no-slots {
          font-size: 14px;
          color: #786798;
          padding: 8px 0;
          text-align: center;
        }
        .rp-cal-wrapper {
          width: 100%;
        }

        /* ── visit card ── */
        .rp-visit-card {
          border: none;
          border-radius: 16px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .rp-vc-top {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .rp-vc-avatar {
          width: 86px;
          height: 86px;
          border-radius: 50%;
          background: #EAE2F3;
          color: #361777;
          font-weight: 700;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .rp-vc-avatar img {
          width: 100%; height: 100%; border-radius: 50%; object-fit: cover;
        }
        .rp-vc-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .rp-vc-name {
          font-size: 16px;
          font-weight: 700;
          color: #361777;
        }
        .rp-vc-service {
          font-size: 13px;
          color: #797B86;
        }
        .rp-vc-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
        }
        .rp-vc-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .rp-vc-datetime-row {
          display: flex;
          align-items: center;
          gap: 24px;
        }
        .rp-vc-meta {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .rp-vc-meta-item {
          display: flex; align-items: center; gap: 8px;
          font-size: 16px; font-weight: 500; color: #4F525B;
        }
        .rp-vc-meta-item svg { color: #4F525B; flex-shrink: 0; }
        .rp-confirm-btn {
          border-radius: 100px !important;
          background: #361777 !important;
          border-color: #361777 !important;
          padding: 12px 24px !important;
          font-size: 15px !important;
          font-weight: 600 !important;
          min-height: 48px !important;
        }
        .rp-confirm-btn:disabled {
          opacity: 0.5;
          background: #361777 !important;
          border-color: #361777 !important;
          cursor: not-allowed;
        }
        .rp-confirm-btn:not(:disabled):hover {
          background: #1A1133 !important;
        }


        .cab-content {
          padding: 0;
          background: transparent;
        }

        /* ── MOBILE ── */
        @media (max-width: 768px) {
          .rp-wrap {
            margin-top: 5px;
          }
          .rp-desktop { display: none !important; }
          .rp-desktop-only { display: none !important; }
          .rp-mobile { display: flex; flex-direction: column; gap: 24px; }
          .rp-back-btn { display: flex; }
          .page-top {
            background: transparent;
            padding: 0 16px;
            border-radius: 0;
          }
          .rp-page-title {
            font-size: 24px;
            margin-bottom: 6px;
          }
          .rp-page-sub { font-size: 16px; margin-bottom: 16px!important; color: #57437E; }

          .rp-mobile-current-section {
            display: flex; flex-direction: column; gap: 12px;
          }
          .rp-mobile-current-label {
            font-size: 16px; color: #797B86; font-weight: 500;
          }

          .rp-visit-card-mobile {
            flex-direction: column;
            align-items: stretch;
            padding: 24px;
            gap: 16px;
            border-radius: 20px;
            border: 1px solid #D6C3E9;
            background: #FFFFFF;
            box-shadow: none;
          }
          .rp-vc-top { gap: 16px; }
          .rp-vc-avatar { width: 56px; height: 56px; font-size: 20px; }
          .rp-vc-name { font-size: 16px; color: #361777; }
          .rp-vc-spec { font-size: 14px; color: #361777; font-weight: 500; }
          .rp-vc-divider { height: 1px; background: #F0E8FA; margin: 0 -24px; }
          
          .rp-vc-actions {
            align-items: flex-start;
            gap: 8px;
          }
          .rp-vc-datetime-row { gap: 0; }
          .rp-vc-meta-item { font-weight: 600; color: #361777; font-size: 15px; }
          .rp-vc-order { font-size: 15px; color: #361777; margin-top: 4px; font-weight: 500; }
          .rp-vc-order strong { color: #361777; font-weight: 700; }
 .rp-field-slots {
          margin-top: 0px;
      }
          .rp-mobile-form-wrap {
            background: #F8F6FC;
            border-radius: 24px;
            padding: 32px 24px;
            display: flex;
            flex-direction: column;
            gap: 32px;
            margin: 0;
          }

          .rp-mobile-form-wrap .rp-field-doctor .rp-field-label {
             text-align: center;
             font-size: 18px;
             margin-bottom: 20px;
          }
          
          .rp-field-inline-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
          }
          .rp-field-inline-header .rp-field-label {
            margin-bottom: 0;
            font-size: 18px;
            color: #361777;
          }
          .rp-field-inline-header .rp-cal-wrapper {
            width: auto;
          }

          .rp-mobile-confirm-btn {
            border-radius: 100px !important;
            background: #361777 !important;
            border-color: #361777 !important;
            padding: 16px !important;
            min-height: 54px !important;
            font-size: 16px !important;
            font-weight: 700 !important;
            width: 100%;
            margin-top: 8px;
          }
        }
      `}</style>
    </div>
  );
};
